import type { SourceMeta } from '../components/upload/SourceMetaForm';
import { auth } from '../config/firebase';
import type {
  ApiResponse,
  ComplianceResult,
  DocumentStatusResponse,
  ExtractedFact,
  FactExtractionResponse,
  FactVerificationResponse,
  InvestigationMessageRequest,
  InvestigationMessageResponse,
  VideoAnalysisResult,
} from '../types';
import { apiRequest, getApiUrl, parseApiResponse } from '../utils/api';
export const uploadPdf = async (
  file: File,
  itemId?: string,
  meta?: SourceMeta,
): Promise<ApiResponse<FactExtractionResponse>> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    // 動画フローと同様に factcheck_item の id を引き渡し、バックエンドが
    // 進捗・結果を直接アイテムに反映できるようにする
    if (itemId) {
      formData.append('item_id', itemId);
    }
    // 資料メタ情報。抽出プロンプトに注入され、主張の文脈補完に使われる
    if (meta) {
      formData.append('meta_doc_type', meta.docType);
      formData.append('meta_title', meta.title);
      formData.append('meta_as_of', meta.asOf);
      formData.append('meta_summary', meta.summary);
    }

    const headers: Record<string, string> = {};

    // Firebase ID Tokenを取得
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    }

    // FormDataの場合、Content-Typeを自動設定させるためheaderからContent-Typeを除外
    const response = await fetch(getApiUrl('/api/upload-pdf'), {
      method: 'POST',
      body: formData,
      headers,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    const data = await parseApiResponse<FactExtractionResponse>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('PDF upload failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
};

export const verifyFacts = async (
  sessionId: string,
  facts: ExtractedFact[],
): Promise<ApiResponse<FactVerificationResponse>> => {
  try {
    const response = await apiRequest('/api/verify-facts', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        facts,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('サーバーエラー詳細:', errorText);
      throw new Error(`Verification failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<FactVerificationResponse>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    console.log('✅ ファクトチェック開始:', data);
    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Fact verification failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Verification failed',
    };
  }
};

// 検証ステータス取得API（再検証でのポーリング用）
export interface VerificationStatusResponse {
  session_id: string;
  status: 'processing' | 'completed' | 'error';
  completed_count: number;
  total_count: number;
  results: Array<{
    claim: string;
    result: string;
    /** ファクト抽出時の原文引用 */
    quote: string;
    /** @deprecated 旧フィールド名。保存済みデータ用 */
    evidence?: string;
    confidence: number;
    source: string;
    reasoning: string;
    originalIndex: number;
    factId: string;
  }>;
  start_time: string;
}

export const getVerificationStatus = async (
  sessionId: string,
): Promise<ApiResponse<VerificationStatusResponse>> => {
  try {
    const response = await apiRequest(`/api/verification-status/${sessionId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ステータス取得エラー詳細:', errorText);
      throw new Error(`Status check failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<VerificationStatusResponse>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Verification status check failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Status check failed',
    };
  }
};

// 映像解析（テロップ・ロゴ・情景）結果の取得（GCSのtelop_analysis/metadataをbackend経由で配信）
export const getVideoAnalysis = async (
  itemId: string,
): Promise<ApiResponse<VideoAnalysisResult>> => {
  try {
    const response = await apiRequest(`/api/video/analysis/${itemId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Analysis fetch failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<VideoAnalysisResult>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Video analysis fetch failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Analysis fetch failed',
    };
  }
};

// 放送規程チェックの実行開始（ユーザー操作・横断）
export const startComplianceCheck = async (
  itemId: string,
): Promise<ApiResponse<{ success: boolean; message: string }>> => {
  try {
    const response = await apiRequest(`/api/compliance/check/${itemId}`, { method: 'POST' });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Compliance start failed: ${response.status} - ${errorText}`);
    }
    const data = await parseApiResponse<{ success: boolean; message: string }>(response);
    return { success: true, data: data ?? { success: true, message: '' } };
  } catch (error) {
    console.error('Compliance start failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Compliance start failed',
    };
  }
};

// 放送規程チェックの結果取得（findings＋ステータス。ポーリング用）
export const getComplianceResult = async (
  itemId: string,
): Promise<ApiResponse<ComplianceResult>> => {
  try {
    const response = await apiRequest(`/api/compliance/${itemId}`, { method: 'GET' });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Compliance fetch failed: ${response.status} - ${errorText}`);
    }
    const data = await parseApiResponse<ComplianceResult>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }
    return { success: true, data };
  } catch (error) {
    console.error('Compliance fetch failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Compliance fetch failed',
    };
  }
};

// アイテムのサムネイル署名URL（動画=代表フレーム / PDF=1ページ目・backendで生成＆キャッシュ）
export const getItemThumbnail = async (itemId: string): Promise<string | null> => {
  try {
    const response = await apiRequest(`/api/items/${itemId}/thumbnail`, { method: 'GET' });
    if (!response.ok) return null;
    const data = (await response.json()) as { url?: string };
    return data.url ?? null;
  } catch {
    return null;
  }
};

// スキャンPDFの非同期OCR処理ステータス取得（ポーリング用）
export const getDocumentStatus = async (
  sessionId: string,
): Promise<ApiResponse<DocumentStatusResponse>> => {
  try {
    const response = await apiRequest(`/api/document-status/${sessionId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ドキュメントステータス取得エラー詳細:', errorText);
      throw new Error(`Document status check failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<DocumentStatusResponse>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return { success: true, data };
  } catch (error) {
    console.error('Document status check failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Status check failed',
    };
  }
};

// 追加調査用API関数

export const investigationChatStream = async (
  request: InvestigationMessageRequest,
  onEvent: (event: string, data: any) => void,
): Promise<void> => {
  try {
    const response = await apiRequest('/api/investigation/chat/stream', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('調査チャットストリームエラー:', errorText);
      throw new Error(`Investigation chat stream failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') {
            onEvent('done', null);
            return;
          }
          try {
            const parsed = JSON.parse(data);
            onEvent(parsed.event, parsed.data);
          } catch (e) {
            console.error('SSE JSONパースエラー:', e, data);
          }
        }
      }
    }
  } catch (error) {
    console.error('Investigation chat stream error:', error);
    onEvent('error', { content: error instanceof Error ? error.message : 'Unknown error' });
  }
};

export const investigationChat = async (
  request: InvestigationMessageRequest,
): Promise<ApiResponse<InvestigationMessageResponse>> => {
  try {
    const response = await apiRequest('/api/investigation/chat', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('調査チャットエラー:', errorText);
      throw new Error(`Investigation chat failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<InvestigationMessageResponse>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Investigation chat failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Investigation chat failed',
    };
  }
};

export const getInvestigationSession = async (sessionId: string): Promise<ApiResponse<any>> => {
  try {
    const response = await apiRequest(`/api/investigation/session/${sessionId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('調査セッション取得エラー:', errorText);
      throw new Error(`Get investigation session failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<any>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Get investigation session failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Get investigation session failed',
    };
  }
};

export const autoRouteMessage = async (
  message: string,
): Promise<ApiResponse<{ recommended_agent: string; message: string }>> => {
  try {
    const response = await apiRequest('/api/investigation/auto-route', {
      method: 'POST',
      body: JSON.stringify({ message }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('エージェント自動選択エラー:', errorText);
      throw new Error(`Auto route failed: ${response.status} - ${errorText}`);
    }

    const data = await parseApiResponse<{ recommended_agent: string; message: string }>(response);
    if (!data) {
      throw new Error('Invalid response format');
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('Auto route failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Auto route failed',
    };
  }
};

// 使用中のAPIエイリアス
export const uploadPdfAPI = uploadPdf;
export const verifyFactsAPI = verifyFacts;
export const investigationChatAPI = investigationChat;

/**
 * 規程PDFを取ってきて、その場で開けるURLにする。
 *
 * もとは public/rules/ に置いて素のURLで開いていたが、ログイン必須にしたため
 * iframe の src に直接URLを入れられない（ヘッダーを付けられない）。
 * いったん中身を取ってから blob のURLにして渡す。
 */
export async function fetchRulePdfUrl(key: string): Promise<string> {
  const headers: Record<string, string> = {};
  const currentUser = auth.currentUser;
  if (currentUser) {
    headers.Authorization = `Bearer ${await currentUser.getIdToken()}`;
  }

  const response = await fetch(getApiUrl(`/api/rules/${key}.pdf`), { headers });
  if (!response.ok) {
    throw new Error(`規程PDFを開けませんでした (${response.status})`);
  }
  return URL.createObjectURL(await response.blob());
}
